<template>
  <div class="card border-0 shadow-none rounded-1 mb-25">
    <div class="card-body p-xl-40">
      <a-table
        :columns="columns"
        :data-source="adjustments"
        :row-key="'id'"
        :loading="loading"
        :pagination="paginationConfig"
        :locale="tableLocale"
        :scroll="{ x: 980 }"
        class="text-nowrap align-middle adjustment-table"
      >
        <template #bodyCell="{ column, record, text }">
          <template v-if="column && column.key === 'date'">
            {{ formatDate(record.created_at) }}
          </template>

          <template v-else-if="column && column.key === 'warehouse_name'">
            <span :title="record.warehouse_name">{{ record.warehouse_name || t("adjustment_page.not_available_short") }}</span>
          </template>

          <template v-else-if="column && column.key === 'items'">
            <span class="badge bg-light text-dark product-count-badge">
              {{ t("adjustment_page.products_count", { count: record.items ? record.items.length : 0 }) }}
            </span>
          </template>

          <template v-else-if="column && column.key === 'note'">
            <span class="note-text" :title="record.note || t('adjustment_page.not_available_short')">
              {{ record.note || t("adjustment_page.not_available_short") }}
            </span>
          </template>

          <template v-else-if="column && column.key === 'actions'">
            <div class="d-flex justify-content-center gap-2 table-actions">
              <button
                class="btn p-0 border-0 action-icon-btn"
                :title="t('adjustment_page.view')"
                data-bs-toggle="modal"
                data-bs-target="#adjustmentDetailsModal"
                @click="$emit('view-details', record)"
              >
                <img src="../../../assets/img/icons/eye.svg" :alt="t('adjustment_page.view')" style="width: 14px" />
              </button>
              <router-link :to="`/edit-adjustment/${record.id}`" :title="t('adjustment_page.edit')" class="btn p-0 border-0 action-icon-btn">
                <img src="../../../assets/img/icons/edit.svg" :alt="t('adjustment_page.edit')" style="width: 14px" />
              </router-link>
              <button
                class="btn p-0 border-0 action-icon-btn"
                :title="t('adjustment_page.delete')"
                @click="$emit('delete-adjustment', record)"
              >
                <img src="../../../assets/img/icons/close.svg" :alt="t('adjustment_page.delete')" style="width: 14px" />
              </button>
            </div>
          </template>

          <template v-else>
            {{ getCellText(text) }}
          </template>
        </template>
      </a-table>

      <div v-if="!loading && adjustments.length" class="d-flex justify-content-end mt-3">
        <span class="fs-13 text-muted">{{ t("adjustment_page.showing_adjustments", { count: adjustments.length }) }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import type { Adjustment } from "../../../services/adjustment";

export default defineComponent({
  name: "AdjustmentList",
  props: {
    adjustments: {
      type: Array as () => Adjustment[],
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    }
  },
  emits: ["view-details", "delete-adjustment"],
  setup() {
    const { t, locale } = useI18n();
    const tableLocale = computed(() => ({
      emptyText: t("adjustment_page.no_adjustments_found"),
    }));

    const columns = computed(() => [
      {
        title: t("adjustment_page.date"),
        dataIndex: "created_at",
        key: "date",
        align: "center" as const,
        width: 220,
      },
      {
        title: t("adjustment_page.reference"),
        dataIndex: "ref",
        key: "ref",
        align: "center" as const,
        width: 150,
      },
      {
        title: t("adjustment_page.warehouse"),
        dataIndex: "warehouse_name",
        key: "warehouse_name",
        align: "center" as const,
        width: 180,
      },
      {
        title: t("adjustment_page.items"),
        dataIndex: "items",
        key: "items",
        align: "center" as const,
        width: 140,
      },
      {
        title: t("adjustment_page.note"),
        dataIndex: "note",
        key: "note",
        align: "center" as const,
        width: 220,
      },
      {
        title: t("adjustment_page.action"),
        key: "actions",
        align: "center" as const,
        width: 120,
      },
    ]);
    const paginationConfig = computed(() => ({
      showSizeChanger: true,
      pageSizeOptions: ["10", "20", "30", "50", "100"],
      defaultPageSize: 10,
      locale: { items_per_page: t("adjustment_page.items_per_page") },
    }));

    const formatDate = (dateString?: string) => {
      if (!dateString) return t("adjustment_page.not_available");
      return new Date(dateString).toLocaleString(locale.value === "km" ? "km-KH" : "en-GB", {
        timeZone: "Asia/Phnom_Penh",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    };

    const getCellText = (value: unknown) => {
      if (typeof value === "string" || typeof value === "number") {
        return value;
      }

      return t("adjustment_page.not_available_short");
    };

    return {
      columns,
      paginationConfig,
      tableLocale,
      formatDate,
      getCellText,
      t,
    };
  },
});
</script>

<style scoped>
.adjustment-table :deep(.ant-table-cell) {
  vertical-align: middle;
}

.adjustment-table :deep(.ant-table-thead > tr > th) {
  white-space: nowrap;
}

.product-count-badge {
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.5rem 0.7rem;
}

.note-text {
  display: inline-block;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.table-actions {
  min-width: 72px;
}

.action-icon-btn {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
